import React, { useState, useMemo, useRef, useLayoutEffect, memo } from 'react';
import { Trophy, Flame, CheckCircle, MessageCircle, ChevronRight, Sparkles, CalendarClock, Info, MoreVertical, Flag, Ban, List, X, Clock, Check } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import AuraIcon from './ui/AuraIcon.jsx';
import { useNavigate } from 'react-router-dom';
import { isTimeSlotActive, TIME_BLOCKS, TIME_BLOCK_ORDER } from '../utils/timeFormatUtils';
import { resolveAssetUrl, resolveAvatarUrl, isGeneratedPlaceholderAvatar } from '../utils/api';
import triggerHaptic from '../utils/haptics';
import { getDisplayLevelProgress } from '../constants/auraLevels';
import { useAuraConfig } from '../hooks/useAuraConfig';
import { buttonClasses } from './ui/Button';
import Avatar from './ui/Avatar.jsx';
import AnimatedNumber from './ui/AnimatedNumber.jsx';
import SegmentedControl from './ui/SegmentedControl.jsx';
import ReportModal from './ReportModal';
import Modal from './ui/Modal.jsx';
import { toast } from './notifications';
import BlockService from '../services/BlockService';
import ChatService from '../services/ChatService';
import ConnectSheet from './connect/ConnectSheet.jsx';
import OtterFigure from './otter/OtterFigure.jsx';

// ─────────────────────────────────────────────────────────────────────────────
// ProfileCardV2 — ruhigeres, weniger "Dating"-lastiges Layout
//   • Rundes Profilbild zentriert auf einer lila Aura-Punkte-Fläche
//   • Match-Score als Ring-Badge (mit %), Level in der Meta-Zeile unter dem Namen
//   • Vorderseite: Tabs Skills / Zeiten / Info; der Skills-Tab zeigt so viele
//     Pill-Reihen, wie in die Karte passen (gematchte Skills zuerst)
//   • Jede Karte laesst sich umdrehen: Listen-Symbol oben rechts (vorne),
//     X oben rechts (hinten). Zusaetzlich dreht das "•••" an einer
//     abgeschnittenen Kategorie direkt auf die Rückseite.
//   • Rückseite (Card-Flip): ALLE Skills, scrollbar
//   • Vernetzen-Button über volle Kartenbreite
// ─────────────────────────────────────────────────────────────────────────────

const prefersReducedMotionGlobal = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Skill-Extraktion (identisch zur ProfileCard, unterstützt alle Backend-Formate)
const extractSkills = (data, fieldName) => {
  if (!data) return [];
  const backendFieldMapping = { offers: 'offeredSkills', seeks: 'soughtSkills' };
  const mapNames = (arr) => arr.map((skill) => {
    if (typeof skill === 'string') return skill;
    if (skill && typeof skill === 'object' && skill.name) return skill.name;
    return String(skill);
  }).filter(Boolean);

  const backendField = data[backendFieldMapping[fieldName]];
  if (Array.isArray(backendField) && backendField.length > 0) return mapNames(backendField);

  const directField = data[fieldName];
  if (Array.isArray(directField) && directField.length > 0) return mapNames(directField);

  const altFieldMapping = { offeredSkills: 'offers', soughtSkills: 'seeks' };
  const altField = data[altFieldMapping[fieldName]];
  if (Array.isArray(altField) && altField.length > 0) return mapNames(altField);

  if (data.profile && data.profile[fieldName]) return extractSkills(data.profile, fieldName);
  return [];
};

const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];
// Zeitbloecke aus der zentralen Tabelle (Uhrzeiten dort pflegen, nicht hier).
const timeSlots = TIME_BLOCK_ORDER.map((blockId) => ({
  label: blockId === 'after' ? 'Nach' : TIME_BLOCKS[blockId].short,
  hours: TIME_BLOCKS[blockId].hours
}));

// Einzelne Pill.
// Okt 2026: Farben wie die Skill-Pille im Spot (ui/Pill) — neutral in Text-2,
// lila nur, wenn der Skill passt. Groesse bleibt bei 30px: die Reihen-Rechnung
// unten (PILL_ROW_H) ist darauf gemessen, und auf dem Telefon zeigt die kleinere
// Pille eine Reihe mehr.
const SkillPill = ({ skill, isMatch, isHighlighted }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full border text-xs font-medium ${
      isHighlighted || isMatch
        ? 'bg-primary-soft border-primary/35 text-primary'
        : 'bg-surface-raised border-border text-foreground-2'
    }`}
  >
    {(isHighlighted || isMatch) && <Sparkles className="w-3 h-3 mr-1 text-primary" />}
    {skill}
  </span>
);

SkillPill.propTypes = {
  skill: PropTypes.string.isRequired,
  isMatch: PropTypes.bool,
  isHighlighted: PropTypes.bool
};

// Gematchte Skills stabil nach vorne sortieren.
const sortMatchedFirst = (skills, userMatchList) => {
  const matchSet = new Set(userMatchList || []);
  return [...skills].sort((a, b) => (matchSet.has(b) ? 1 : 0) - (matchSet.has(a) ? 1 : 0));
};

// Rendert eine Pill-Liste (gematchte zuerst). Genutzt in Teaser (vorne) und Rückseite.
const renderPills = (skills, userMatchList, searchTerm) => sortMatchedFirst(skills, userMatchList).map((skill, i) => {
  const isMatch = (userMatchList || []).includes(skill);
  const isHighlighted = !!(searchTerm && skill.toLowerCase().includes(searchTerm.toLowerCase()));
  return <SkillPill key={i} skill={skill} isMatch={isMatch} isHighlighted={isHighlighted} />;
});

// Masse einer Pill-Reihe. Die Pills sind `whitespace-nowrap` und brechen deshalb NIE
// intern um — eine Reihe ist immer exakt gleich hoch, die Rechnung geht ohne Messen auf.
const PILL_ROW_H = 30;   // px-3 py-1.5 text-xs
const PILL_GAP = 8;      // gap-2

// Was eine Kategorie ausser den Pills kostet: Überschrift (text-xs, 16px Zeile)
// + mb-2. Im Browser gegen das gebaute CSS nachgemessen, nicht geschaetzt.
const CATEGORY_HEAD_H = 24;
// space-y-4 zwischen den beiden Kategorien.
const CATEGORY_GAP = 16;

// Reihen -> Pixel und zurueck.
export const rowsToHeight = (rows) => PILL_ROW_H + (rows - 1) * (PILL_ROW_H + PILL_GAP);
const heightToRows = (px) => Math.floor((px - PILL_ROW_H) / (PILL_ROW_H + PILL_GAP)) + 1;

// Wieviele Pill-Reihen passen JE KATEGORIE in die gegebene Hoehe?
//
// Bis August 2026 stand hier eine feste Zahl (TEASER_MAX_H = 68, also zwei Reihen) —
// unabhaengig davon, wie gross das Telefon war. Auf einem iPhone 15 Pro blieb dadurch
// ein Drittel der Karte leer, waehrend die Skill-Liste schon nach zwei Reihen abriss.
// Jetzt wird der vorhandene Platz halbiert und in Reihen umgerechnet.
//
// Untergrenze EINE Reihe, nicht zwei. Zwei Reihen zu erzwingen, wo nur eine
// hinpasst, heisst: die zweite wird vom overflow-hidden mittendurch geschnitten —
// eine halbe Pille sieht nach Defekt aus. Passt nur eine Reihe, ist eine Reihe
// plus "•••" die ehrliche Anzeige. Obergrenze vier: mehr liest niemand auf einer
// Uebersichtskarte, und der Rest steht ohnehin einen Tipp entfernt auf der
// Rueckseite.
export const MIN_TEASER_ROWS = 1;
export const MAX_TEASER_ROWS = 4;

// Ein paar Pixel Sicherheitsabstand. Die Schriftgroessen sind fluid
// (tailwind.config.js), die Zeilenhoehe der Ueberschrift kann also je nach
// Geraetebreite um ein, zwei Pixel abweichen. Der Fehler darf nur in EINE
// Richtung gehen: zu grosszuegig gerechnet kostet hoechstens eine Reihe, zu
// knapp gerechnet schneidet eine Pille mittendurch.
const TEASER_SAFETY = 4;

// Beide Kategorien bekommen gleich viel Platz — auch wenn eine davon nur zwei
// Skills hat. Sie sind die zwei Haelften desselben Matches ("kann helfen" /
// "moechte lernen"); eine davon zusammenzuquetschen, weil die andere voller ist,
// wuerde die Karte von Profil zu Profil anders aussehen lassen.
export const fitTeaserRows = (paneHeight) => {
  if (!paneHeight) return MIN_TEASER_ROWS;
  const budget = (paneHeight - 2 * CATEGORY_HEAD_H - CATEGORY_GAP - TEASER_SAFETY) / 2;
  const rows = heightToRows(budget);
  return Math.max(MIN_TEASER_ROWS, Math.min(MAX_TEASER_ROWS, rows));
};

// Was die beiden Kategorien bei dieser Reihenzahl mindestens brauchen.
export const teaserNeeds = (rows) => 2 * (CATEGORY_HEAD_H + rowsToHeight(rows)) + CATEGORY_GAP;

// ─── Kopfhoehe: das Portrait bekommt, was uebrig ist ────────────────────────
//
// Bis September 2026 hing die Avatar-Groesse an der Viewport-HOEHE
// (clamp(5.5rem, 17vh, 9rem) in index.css). Das ging im mobilen Browser schief:
// `vh` ist die Bildschirmhoehe OHNE Abzug fuer Adress- und Werkzeugleiste. Auf
// einem iPhone 16 Pro Max (956px) rechnete der Avatar also mit 17 % von 956 und
// blieb bei seinen 144px — waehrend die Karte, die richtig mit der sichtbaren
// Hoehe rechnet, nur noch rund 500px hoch war. Der Kopf frass die halbe Karte,
// und von den Skills war eine einzige Pill-Reihe zu sehen.
//
// Jetzt wird nicht mehr geschaetzt, sondern von hinten gerechnet: Tabs, Knopf
// und der Rest des Kopfes (Name, Meta-Zeile, Innenabstaende) haben ihren Bedarf,
// der Inhalt bekommt seinen WUNSCH — zwei Pill-Reihen pro Kategorie — und was
// dann noch da ist, darf das Portrait sein. Alle Zahlen dafuer werden am
// gerenderten Kasten gemessen (siehe measure() im Bauteil), nicht geraten.
//
// Wichtig fuer die Stabilitaet: Die Messgroessen sind von der Avatar-Groesse
// UNABHAENGIG. Der Kopf ist flex-shrink-0, der Inhalt flex-1 — ihre Summe ist
// immer Kartenhoehe minus Tabs minus Knopf, egal wie gross das Portrait ist.
// Deshalb kann sich die Rechnung nicht selbst aufschaukeln.

// Der Wunsch des Inhaltsbereichs: zwei Reihen je Kategorie. Weniger als zwei
// Reihen ist die Grenze, ab der die Karte "leer" wirkt (genau das Problem in
// der Meldung vom 4. September); mehr als zwei liest auf einer Uebersichtskarte
// niemand, und der Rest steht einen Tipp entfernt auf der Rueckseite.
const CONTENT_WISH_ROWS = 2;

// Kleinste Inhaltshoehe, bei der fitTeaserRows() diese Reihenzahl auch wirklich
// vergibt. teaserNeeds() allein waere hier falsch: fitTeaserRows rechnet
// zusaetzlich TEASER_SAFETY ab. Ein Kopf, der genau auf teaserNeeds(2) zielt,
// landet damit zuverlaessig bei EINER Reihe — also genau da, wo er nicht
// hinsollte. Zwei Pixel, die den ganzen Zweck der Rechnung kippen.
export const contentNeedForRows = (rows) => teaserNeeds(rows) + TEASER_SAFETY;

// Unter- und Obergrenze des Portraits. 144px (w-36) war und bleibt der
// Wunschwert — auf dem Desktop und in der Capacitor-App aendert sich damit
// nichts. 80px ist die Grenze, ab der ein Portrait nicht mehr als Portrait
// wirkt; darunter wird nicht weiter verkleinert, dann darf die Vorschau lieber
// scrollen (teaserScrolls).
const AVATAR_MIN = 80;
const AVATAR_MAX = 144;

// Obergrenze im kompakten Kopf. Sie ist kein Geschmackswert, sondern der Preis
// fuer die Schwelle darunter: Der kompakte Kopf spart 17px an Abstaenden und
// Schriftgroesse (im Browser gemessen: 100px Kopf-Rest normal, 83px kompakt).
// Ohne eine eigene Obergrenze wuerde das Portrait an der Schwelle SPRINGEN —
// beim Verkleinern des Fensters wuerde das Bild groesser, was aussieht wie ein
// Fehler. 128px ist genau die Groesse, die eine 560px-Karte ohne kompakten Kopf
// ergibt; damit geht die Kurve an der Schwelle stufenlos weiter.
const AVATAR_MAX_COMPACT = 128;

// Ab dieser Kartenhoehe wird der Kopf kompakt: kleinere Innenabstaende, Name
// eine Stufe kleiner, kleineres Match-Ring-Abzeichen. Die Schwelle haengt
// ALLEIN an der Kartenhoehe (einem von aussen vorgegebenen Wert) und nicht an
// der berechneten Avatar-Groesse — sonst koennte sie zwischen kompakt und
// normal hin- und herspringen.
// 560px: Eine 620px-Karte (Desktop, Tablet, Capacitor-App) bleibt normal, eine
// Karte im mobilen Browser (dort rund 480-520px) wird kompakt.
const COMPACT_CARD_HEIGHT = 560;

// Wieviel Platz bleibt fuer das Portrait?
//   cardHeight   - Hoehe der Karte (von der Folie oder der clamp()-Formel)
//   cardChrome   - Tab-Leiste + Vernetzen-Knopf
//   headerChrome - alles im Kopf AUSSER dem Portrait (Name, Meta, Abstaende)
//   contentNeed  - der Wunsch des Inhaltsbereichs inkl. seiner Innenabstaende
export const fitAvatarSize = ({ cardHeight, cardChrome, headerChrome, contentNeed, max = AVATAR_MAX }) => {
  if (!cardHeight) return null;
  const room = cardHeight - cardChrome - headerChrome - contentNeed;
  return Math.round(Math.max(AVATAR_MIN, Math.min(max, room)));
};

// Teaser-Kategorie auf der Vorderseite: Überschrift + Pills, auf `maxRows` Reihen begrenzt
// (maxHeight + overflow-hidden), gematchte Skills zuerst. Ragt der Inhalt über diese
// Reihen hinaus (gemessen: scrollHeight > clientHeight), erscheint unten rechts ein
// klickbares "•••", das die Karte auf die Rückseite mit allen Skills dreht (onShowAll).
const TeaserCategory = ({ label, skills, userMatchList, searchTerm, maxRows, onShowAll }) => {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && innerRef.current) ro.observe(innerRef.current);
    return () => { if (ro) ro.disconnect(); };
  }, [skills, userMatchList, maxRows]);

  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-foreground-2 mb-2">
        {label} {skills.length > 0 && <span className="text-muted-foreground">({skills.length})</span>}
      </p>
      {skills.length > 0 ? (
        <div className="relative">
          <div ref={wrapRef} className="overflow-hidden" style={{ maxHeight: `${rowsToHeight(maxRows)}px` }}>
            <div ref={innerRef} className="flex flex-wrap gap-2">
              {renderPills(skills, userMatchList, searchTerm)}
            </div>
          </div>
          {clipped && (
            <button
              type="button"
              onClick={onShowAll}
              aria-label={`Alle ${skills.length} Skills anzeigen`}
              style={{ height: `${PILL_ROW_H}px` }}
              className="absolute bottom-0 right-0 flex items-center pl-8 pr-1 text-base font-bold text-primary bg-gradient-to-l from-surface via-surface to-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            >
              •••
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Noch keine Angaben</p>
      )}
    </div>
  );
};

TeaserCategory.propTypes = {
  label: PropTypes.string.isRequired,
  skills: PropTypes.arrayOf(PropTypes.string).isRequired,
  userMatchList: PropTypes.arrayOf(PropTypes.string),
  searchTerm: PropTypes.string,
  maxRows: PropTypes.number.isRequired,
  onShowAll: PropTypes.func.isRequired
};

// Vollständige Skill-Liste (Rückseite): Überschrift + alle Pills, natürlich umbrechend,
// gematchte zuerst.
const AllSkillsList = ({ label, skills, userMatchList, searchTerm }) => (
  <div>
    <p className="text-xs font-medium tracking-wide text-foreground-2 mb-2">
      {label} {skills.length > 0 && <span className="text-muted-foreground">({skills.length})</span>}
    </p>
    {skills.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {renderPills(skills, userMatchList, searchTerm)}
      </div>
    ) : (
      <p className="text-xs text-muted-foreground">Noch keine Angaben</p>
    )}
  </div>
);

AllSkillsList.propTypes = {
  label: PropTypes.string.isRequired,
  skills: PropTypes.arrayOf(PropTypes.string).isRequired,
  userMatchList: PropTypes.arrayOf(PropTypes.string),
  searchTerm: PropTypes.string
};

// Hoehe der Karte. Die Formel steht als .profile-card-height in index.css
// (dort auch die Begruendung fuer jeden Abzug, fuer `svh` und dafuer, warum es
// KEINE Tailwind-Klasse ist: Bis September 2026 war sie hier aus zwei Strings
// zusammengesetzt, Tailwind hat sie deshalb nie erzeugt, und die Karte hatte in
// den Suchergebnissen gar keine Hoehe — sie wuchs mit ihrem Inhalt).
// Ueberschreibbar per heightClass fuer Umgebungen mit anderen Abzuegen (z.B.
// die Willkommens-Tour, die die Karte ohne Seiten- und Navigationsleiste zeigt).
const DEFAULT_CARD_HEIGHT = 'profile-card-height';

// Alternative zur eigenen Rechnung: `fill` laesst die Karte die Hoehe ihres
// Elternelements uebernehmen. Genau das braucht die Runden-Ansicht — dort gibt
// die Folie (.match-slide) die Hoehe vor: auf dem Telefon der ganze Bildschirm,
// ab Tablet feste 620px. Die Karte rechnet dann gar nichts mehr selbst, und die
// Frage "wieviel zieht man oben ab" stellt sich an genau einer Stelle.
const FILL_CARD_HEIGHT = 'h-full';

/**
 * Teile aus `otterOutfit` eines fremden Profils ({ kopf: 'beanie', … }).
 * null = kein Otter (Feld fehlt oder ist kaputt), [] = Otter ohne Teile.
 */
export const profileOtterParts = (outfit) => {
  if (!outfit || typeof outfit !== 'object' || Array.isArray(outfit)) return null;
  return Object.values(outfit).filter((part) => typeof part === 'string' && part.length > 0);
};

const ProfileCardV2 = ({
  profile,
  currentUser,
  searchTerm,
  isPreviewMode = false,
  hideMatchScore = false,
  hideConnectButton = false,
  heightClass = DEFAULT_CARD_HEIGHT,
  fill = false,
  onSnooze,
  onBlocked,
  origin
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('skills');
  const [menuOpen, setMenuOpen] = useState(false);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  // Wieviel Platz hat der Inhaltsbereich zwischen Tab-Leiste und Vernetzen-Knopf?
  // Die Karte hat eine feste Hoehe (h-full der Folie), der Inhalt muss sich also
  // danach richten — und nicht umgekehrt die Karte scrollbar machen.
  //
  // Gemessen wird der INHALTSKASTEN, nicht clientHeight. clientHeight schliesst die
  // eigene Innenpolsterung (py-2) mit ein, die Skill-Liste bekommt sie aber nicht:
  // Sie ist ein Kind und lebt im Inhaltskasten. Bis August 2026 stand hier
  // clientHeight blank — die Karte rechnete damit rund 16px mehr Platz aus, als es
  // gab, waehlte in einem schmalen Streifen eine Reihe zuviel, und die letzte
  // Pill-Reihe wurde vom overflow-hidden angeschnitten. Schlimmer noch: Die
  // Notbremse teaserScrolls verglich gegen dieselbe zu grosse Zahl und griff
  // deshalb genau dann nicht, wenn sie gebraucht wurde.
  const cardRef = useRef(null);
  const headerRef = useRef(null);
  const avatarRef = useRef(null);
  const contentRef = useRef(null);
  const teaserPaneRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Groesse des Portraits, aus der Kartenhoehe zurueckgerechnet (siehe
  // fitAvatarSize oben). `null` = noch nicht gemessen; dann gilt die
  // clamp()-Rueckfallebene aus index.css.
  const [avatarSize, setAvatarSize] = useState(null);
  const [cardHeight, setCardHeight] = useState(0);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return undefined;

    const measure = () => {
      const cs = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(content) : null;
      const padding = cs
        ? (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
        : 0;
      setContentHeight(Math.max(0, content.clientHeight - padding));

      const card = cardRef.current;
      const header = headerRef.current;
      const avatarBox = avatarRef.current;
      if (!card || !header || !avatarBox) return;

      const measuredCardHeight = card.clientHeight;
      setCardHeight(measuredCardHeight);
      const compact = measuredCardHeight < COMPACT_CARD_HEIGHT;
      const headerOuter = header.offsetHeight;
      // Kopf und Inhalt teilen sich genau den Rest der Karte (flex-shrink-0 +
      // flex-1). Ihre Summe von der Kartenhoehe abzuziehen ergibt also, was
      // Tab-Leiste und Vernetzen-Knopf zusammen kosten — unabhaengig davon, wie
      // gross das Portrait gerade ist.
      const cardChrome = Math.max(0, measuredCardHeight - headerOuter - content.offsetHeight);
      const headerChrome = Math.max(0, headerOuter - avatarBox.offsetHeight);

      setAvatarSize(fitAvatarSize({
        cardHeight: measuredCardHeight,
        cardChrome,
        headerChrome,
        contentNeed: contentNeedForRows(CONTENT_WISH_ROWS) + padding,
        max: compact ? AVATAR_MAX_COMPACT : AVATAR_MAX
      }));
    };

    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(content);
      if (cardRef.current) ro.observe(cardRef.current);
    }
    return () => { if (ro) ro.disconnect(); };
  }, []);

  // Kompakter Kopf auf kurzen Karten: kleinere Innenabstaende, Name eine Stufe
  // kleiner, kleineres Match-Abzeichen. Haengt bewusst nur an der Kartenhoehe
  // (siehe COMPACT_CARD_HEIGHT), damit die Messung nicht mit sich selbst ringt.
  const compactHeader = cardHeight > 0 && cardHeight < COMPACT_CARD_HEIGHT;

  const teaserRows = useMemo(() => fitTeaserRows(contentHeight), [contentHeight]);

  // Rueckfallebene fuer sehr niedrige Geraete (iPhone SE und aehnlich): Dort
  // passen selbst zwei EINZEILIGE Kategorien nicht mehr in die Karte. Abschneiden
  // waere dort das Falsche — eine halb sichtbare Ueberschrift "Möchte lernen"
  // sieht nach Fehler aus. In diesem einen Fall darf die Vorschau also doch
  // scrollen, nur eben ohne grauen Systembalken. Auf allen normal hohen Telefonen
  // ist der Wert false und es bleibt beim Zuschneiden.
  const teaserFitsByMath = !(contentHeight > 0 && teaserNeeds(teaserRows) > contentHeight);

  // Gegenprobe an der gebauten Seite statt nur auf dem Papier.
  //
  // Die Rechnung oben stimmt fuer sich (ProfileCardV2.rows.test.js nagelt sie
  // fest), sie haengt aber an EINEM gemessenen Wert: contentHeight. Faellt der
  // zu grosszuegig aus — und auf einem iPad-Fenster tat er das —, verspricht
  // die Rechnung Platz, den es nicht gibt, und die zweite Kategorie wird an
  // einer harten Kante abgeschnitten. Das sah nach kaputter App aus.
  //
  // Deshalb wird zusaetzlich gemessen, was `TeaserCategory` fuer ihre Pillen
  // ohnehin schon tut: Ist der Inhalt hoeher als die Flaeche? Dann darf die
  // Vorschau scrollen — lieber ein weicher Auslauf als ein Schnitt.
  const [teaserOverflows, setTeaserOverflows] = useState(false);

  useLayoutEffect(() => {
    const pane = teaserPaneRef.current;
    if (!pane) { setTeaserOverflows(false); return undefined; }

    // Toleranz 4px statt 1px: Zwei Kategorie-Ueberschriften mit Subpixel-
    // Zeilenhoehe rundeten sonst auf dem iPhone zu einem "Ueberlauf" von 2px
    // auf, und die Karte schaltete grundlos auf Scrollen samt Ausblend-Maske
    // um. Echte Ueberlaeufe (eine Pillenreihe ist rund 36px) bleiben erkannt.
    const check = () => setTeaserOverflows(pane.scrollHeight > pane.clientHeight + 4);
    check();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    if (ro) ro.observe(pane);
    return () => { if (ro) ro.disconnect(); };
  }, [teaserRows, activeTab, contentHeight]);

  const teaserScrolls = !teaserFitsByMath || teaserOverflows;


  // Flächen/Ränder/Schatten kommen jetzt aus dem Token-System (Token-Klassen wie
  // bg-surface / border-border / shadow-token-*). Dadurch reagiert die Karte
  // automatisch auf alle drei Theme-Achsen (Dark, High-Contrast, Neobrutalism).
  // Dekorative Aura nutzt rgb(var(--color-primary) / α) statt fester Hex-Werte.

  const prefersReducedMotion = prefersReducedMotionGlobal;

  // Lokaler Stand nach „Anfrage senden“ bzw. „Annehmen“ (Okt 2026): Der Knopf
  // springt sofort um, ohne auf ein Neuladen der Liste zu warten.
  const [localConnection, setLocalConnection] = useState(null);
  const [connectSheetOpen, setConnectSheetOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const connectionStatus = localConnection?.status || profile.connectionStatus?.status || 'none';
  const connectionChatId = localConnection?.chatId || profile.connectionStatus?.chatId || null;
  const connectionRequestId = profile.connectionStatus?.requestId || null;

  // Sichere Datenaufbereitung
  const safeProfile = useMemo(() => ({
    ...profile,
    offers: extractSkills(profile, 'offers'),
    seeks: extractSkills(profile, 'seeks'),
    auraLevel: profile.auraLevel || profile.profile?.auraLevel || 1,
    peakLevel: profile.peakLevel
      || profile.auraStats?.peakLevel
      || profile.profile?.auraStats?.peakLevel || null,
    auraPoints: profile.auraPoints
      || profile.auraStats?.currentPoints
      || profile.profile?.auraStats?.currentPoints || 0,
    auraStats: profile.auraStats || profile.profile?.auraStats || { completedSessions: 0, currentStreak: 0 },
    image: profile.image || profile.avatar || profile.profile?.avatar || '',
    bio: profile.bio || profile.profile?.bio || profile.aboutMe || '',
    schoolClass: profile.schoolClass || profile.profile?.schoolClass || profile.grade || '',
    availability: profile.availability || profile.profile?.availability || {}
  }), [profile]);

  const safeCurrentUser = currentUser || {};
  // Fortschritt zum naechsten Level mit den Schwellen der Schule (Standard
  // 500/1000/2000/5000). Bis September 2026 rechnete die Karte "% 100": bei
  // 557 AP stand da "43 AP bis Level 2", und der Balken sprang alle 100 Punkte
  // zurueck. Danach rechnete sie mit den Standardwerten, egal was die Schule
  // eingestellt hatte - jetzt mit denselben Schwellen wie die Aura-Seite.
  const { levelThresholds } = useAuraConfig();
  //
  // Aura v2 (Okt 2026): Das gespeicherte `auraLevel` hinkt hinterher (es wird nur
  // bei Aura-Buchungen nachgezogen, nicht bei geaenderten Schwellen). Angezeigt
  // wird das Level aus den Punkten, mindestens das hoechste je erreichte
  // (peakLevel, falls die Payload es mitbringt).
  const auraProgress = getDisplayLevelProgress(
    Number(safeProfile.auraPoints) || 0,
    levelThresholds,
    safeProfile.peakLevel,
  );
  const currentLevel = auraProgress.currentLevel || 1;
  const matchScore = profile.matchScore || 0;
  const completedSessions = safeProfile.auraStats?.completedSessions || 0;
  const currentStreak = safeProfile.auraStats?.currentStreak || 0;

  // "Neu dabei" nur, wenn das Profil WIRKLICH leer ist — sonst behauptet die Karte
  // gleichzeitig "erfahren" (198 AP, 12 Tage Streak) und "neu hier".
  const isNewcomer = completedSessions === 0 && currentStreak === 0 && safeProfile.auraPoints === 0;

  // 'card'-Variante (384px WebP) statt Vollbild — deutlich kleiner bei gleicher Optik (144px Anzeige)
  const stableImageUrl = useMemo(() => {
    if (!safeProfile.image) return '';
    return resolveAvatarUrl(safeProfile.image, 'card');
  }, [safeProfile.image]);

  const fullImageUrl = useMemo(() => {
    if (!safeProfile.image || isGeneratedPlaceholderAvatar(safeProfile.image)) return '';
    return resolveAssetUrl(safeProfile.image);
  }, [safeProfile.image]);

  // Letztes Glied der Fallback-Kette: lokale Initialen ueber ui/Avatar statt eines
  // Aufrufs an ui-avatars.com (der sonst den Klarnamen an einen Fremdserver schickt).
  const [avatarFailed, setAvatarFailed] = useState(false);

  const hasValidUserId = !!(safeProfile.userId || safeProfile.tenant_users?.id);

  // Otter mit Teilen (Aura v2, Okt 2026): Das Backend legt `otterOutfit` nur in
  // fremde Profile, wenn die Person ihren Otter sichtbar geschaltet hat und zur
  // selben Schule gehoert ({ kopf: 'beanie', ... }, Teile statt Schluessel).
  // Fehlt das Feld, zeigt die Karte keinen Otter. Das eigene Profil bleibt wie bisher.
  const isOwnProfile = !!safeCurrentUser.id
    && (safeProfile.userId || safeProfile.tenant_users?.id) === safeCurrentUser.id;
  const otterParts = profileOtterParts(profile.otterOutfit);
  const showOtter = !isOwnProfile && otterParts !== null;

  // Vernetzen ohne Seitenwechsel (Okt 2026): ein Blatt mit optionaler Nachricht
  // (components/connect/ConnectSheet). Vorher fuehrte der Knopf auf /connect/:id;
  // die Seite bleibt als Rueckfall fuer direkte Aufrufe bestehen.
  const handleConnectClick = () => {
    const recipientUserId = safeProfile.userId || safeProfile.tenant_users?.id;
    if (!recipientUserId) {
      toast.error('Dieses Profil ist derzeit nicht verfügbar.');
      return;
    }
    triggerHaptic('light');
    setConnectSheetOpen(true);
  };

  // Eine Anfrage von dieser Person liegt schon vor: direkt annehmen. Bis Oktober
  // 2026 fuehrte auch dieser Knopf auf die Vernetzen-Seite, die eine NEUE
  // Anfrage schickte — und das Backend lehnte sie ab (409, Anfrage existiert).
  const handleAcceptClick = async () => {
    if (!connectionRequestId) {
      handleConnectClick();
      return;
    }
    setIsAccepting(true);
    try {
      const response = await ChatService.acceptChatRequest(connectionRequestId);
      const chatId = response?.data?.chatId || response?.chatId || null;
      triggerHaptic('success');
      setLocalConnection({ status: 'connected', chatId });
    } catch (error) {
      toast.error('Die Anfrage konnte nicht angenommen werden. Bitte versuche es gleich noch einmal.');
    } finally {
      setIsAccepting(false);
    }
  };

  // Blockieren fragt über Modal.Confirm nach (kein window.confirm mehr, Sep 2026).
  const handleBlockClick = () => {
    const targetUserId = safeProfile.userId || safeProfile.tenant_users?.id;
    if (!targetUserId) return;
    setMenuOpen(false);
    setBlockConfirmOpen(true);
  };

  const confirmBlock = async () => {
    const targetUserId = safeProfile.userId || safeProfile.tenant_users?.id;
    setBlockConfirmOpen(false);
    if (!targetUserId) return;
    try {
      await BlockService.blockUser(targetUserId);
      setIsBlocked(true);
      setMenuOpen(false);
      toast.success('Person blockiert');
      // In der Match-Runde: Karte sofort aus der Runde nehmen (der Server
      // filtert Blockierte beim naechsten Laden ohnehin heraus).
      onBlocked?.(targetUserId);
    } catch (err) {
      toast.error('Blockieren fehlgeschlagen');
    }
  };

  // Card-Flip mit Haptik (iOS + Android): derselbe "Snap" in beide Richtungen
  // (seit Oktober 2026; vorher zurück nur ein leichter Tick, zwei Gefühle für eine Geste).
  const flipToBack = () => { triggerHaptic('flip'); setIsFlipped(true); };
  const flipToFront = () => { triggerHaptic('flip'); setIsFlipped(false); };

  // Gemeinsame Zeitslots berechnen (für Highlight in Zeiten-Tab)
  const { sharedSlots } = useMemo(() => {
    const shared = new Set();
    const userAvail = safeCurrentUser?.availability;
    const profileAvail = safeProfile?.availability;
    if (userAvail && profileAvail) {
      weekDays.forEach((day) => {
        timeSlots.forEach((slot) => {
          if (isTimeSlotActive(userAvail, day, slot.hours) && isTimeSlotActive(profileAvail, day, slot.hours)) {
            shared.add(`${day}-${slot.label}`);
          }
        });
      });
    }
    return { sharedSlots: shared };
  }, [safeCurrentUser?.availability, safeProfile?.availability]);

  // Match-Ring (SVG) — Farbabstufung wie Aura
  const ringRadius = 22;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference * (1 - Math.min(matchScore, 100) / 100);
  // Match-Ring immer in Primary (Okt 2026, wie im Spot): Die Zahl in der Mitte
  // sagt, wie gut es passt — eine zweite Abstufung ueber die Farbe war doppelt.

  const tabs = [
    { id: 'skills', label: 'Skills', Icon: Sparkles },
    { id: 'times', label: 'Zeiten', Icon: CalendarClock },
    { id: 'info', label: 'Info', Icon: Info }
  ];

  return (
    <>
    {/* ── Card-Flip-Bühne (Perspektive; KEIN overflow-hidden hier) ──────────── */}
    {/* -webkit-Präfixe sind für iOS-WKWebView Pflicht — sonst wird preserve-3d
        flachgedrückt und die abgewandte Rückseite bleibt sichtbar (Overlay-Bug). */}
    {/* Bei `fill` reichen Buehne und Dreh-Container die Hoehe des Elternelements
        durch (h-full auf jeder Ebene, sonst laeuft die Prozentangabe der
        Vorderseite ins Leere). */}
    <div
      // Breite haengt an `fill`, weil `fill` genau bedeutet: "ich stecke in einer
      // .match-slide". Dort gibt der Container die Breite vor — am Telefon die
      // Bildschirmbreite, ab Tablet die Rasterspalte —, und die Karte soll sie
      // fuellen. Bis September 2026 klebte sie auch dort auf max-w-sm (384px):
      // Im Raster ergab das einen schmalen Streifen in einer breiten Spalte, und
      // in einem schmalen iPad-Fenster passten lange Skill-Namen nicht mehr in
      // eine Zeile, worauf die Vorschau auf Scrollen umschaltete und Chips
      // angeschnitten aussahen. max-w-lg ist die Obergrenze fuer den einspaltigen
      // Fall; ausserhalb der Runde (Suche, Willkommens-Tour) bleibt es bei
      // max-w-sm, dort steht die Karte allein.
      className={`relative w-full mx-auto ${fill ? 'h-full max-w-lg' : 'max-w-sm'}`}
      style={{ perspective: '1200px', WebkitPerspective: '1200px' }}
    >
      <div
        className={`relative w-full ${fill ? 'h-full' : ''}`}
        style={{
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          WebkitTransform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transition: prefersReducedMotion ? 'none' : 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
      {/* ═══ VORDERSEITE ═══════════════════════════════════════════════════ */}
      {/* pointer-events nur aktiv, wenn NICHT geflippt — sonst fängt die (unsichtbare)
          Vorderseite den Klick auf den "zurück"-Button der Rückseite ab. */}
      {/* Die Vorderseite gibt der Flip-Buehne ihre Hoehe (die Rueckseite ist
          absolute inset-0). Eine definierte Hoehe bleibt noetig, damit die Karte
          beim Tab-Wechsel nicht springt.
          Vorher stand hier fix h-[620px] — das passte auf einem iPhone SE nicht
          unter Kopf- und Navigationsleiste. Jetzt: hoechstens 620px (Desktop und
          grosse Geraete unveraendert), sonst der tatsaechlich verfuegbare Platz,
          berechnet aus denselben Variablen, die MainScreen fuer sein <main> nutzt.
          Untergrenze 22rem, damit die Karte im Querformat nicht auf einen Streifen
          zusammenfaellt — abgeschnitten wird dabei nichts, alle drei Tabs scrollen.
          Aug 2026: Die Formel zog nur Kopf- und Navigationsleiste ab und vergass
          das Extra-Polster, den Seitenkopf und das Session-Banner — dadurch lag
          der Vernetzen-Knopf am unteren Kartenrand hinter der Navigationsleiste.
          Beides steckt jetzt in --match-card-reserve und --banner-height. */}
      <div
        ref={cardRef}
        className={`relative w-full rounded-3xl overflow-hidden flex flex-col bg-surface border border-border shadow-token-lg ${fill ? FILL_CARD_HEIGHT : heightClass}`}
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          opacity: isFlipped ? 0 : 1,
          transition: prefersReducedMotion ? 'none' : 'opacity 0s linear 0.3s',
          pointerEvents: isFlipped ? 'none' : 'auto',
          // Gemessener Wert fuer .profile-card-avatar (index.css). Solange nicht
          // gemessen ist, greift dort die clamp()-Rueckfallebene.
          ...(avatarSize ? { '--profile-card-avatar-size': `${avatarSize}px` } : {})
        }}
      >
      {/* Der Aura-Lichtschein hinter dem Portrait (radialer Lila-Verlauf) ist seit
          Oktober 2026 weg: Im Werbespot steht die Karte ruhig auf Weiss, und CD
          Regel 2 schliesst Verlaeufe aus. */}

      {/* ── Kopf: zentriertes rundes Bild ────────────────────────────────── */}
      {/* Punkte-Textur und weicher Aura-Lichtfleck wurden entfernt (Juli 2026):
          zusammen mit dem Glow am Bildrahmen waren das drei Effekt-Ebenen auf
          derselben kleinen Flaeche. Das Specular-Highlight darueber bleibt. */}
      <div ref={headerRef} className={`relative px-6 flex-shrink-0 ${compactHeader ? 'pt-4 pb-2' : 'pt-6 pb-3'}`}>
        {/* Umdrehen oben rechts — auf JEDER Karte, nicht nur wenn eine Kategorie
            abgeschnitten ist.
            Vorher fuehrte nur das "•••" an einer zu langen Liste auf die
            Rueckseite: Bei einem Profil mit wenigen Skills gab es also gar keinen
            Weg dorthin, und man musste erst raten, ob es eine Rueckseite gibt.
            Das Listen-Symbol sagt, was kommt (die vollstaendige Liste) — ein
            gedrehter Pfeil sah nach "rueckgaengig" aus. Zurueck geht es hinten
            ueber ein X, wie bei allem, was sich oeffnet und wieder schliesst.
            Hier sass bis August 2026 das Level-Abzeichen; es steht jetzt in der
            Meta-Zeile unter dem Namen und kostet damit keine eigene Hoehe. */}
        <button
          type="button"
          onClick={flipToBack}
          aria-label="Alle Skills anzeigen"
          className={`absolute ${compactHeader ? 'top-3' : 'top-5'} right-6 z-20 p-2 before:absolute before:-inset-1.5 before:content-[''] rounded-full bg-surface border border-border text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
        >
          <List className="w-4 h-4" aria-hidden="true" />
        </button>

        {/* Melden/Blockieren-Menü oben links (nicht im Vorschau-Modus / eigenes Profil) */}
        {!isPreviewMode && hasValidUserId && (safeProfile.userId || safeProfile.tenant_users?.id) !== safeCurrentUser.id && (
          <div className={`absolute ${compactHeader ? 'top-3' : 'top-5'} left-6 z-20`}>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Weitere Optionen"
              aria-expanded={menuOpen}
              className="relative p-2 before:absolute before:-inset-1.5 before:content-[''] rounded-full bg-surface border border-border text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <MoreVertical className="w-4 h-4" aria-hidden="true" />
            </button>
            {menuOpen && (
              <div className="absolute left-0 mt-2 w-44 rounded-xl bg-surface border border-border shadow-token-lg overflow-hidden">
                {/* "Später zeigen" steht bewusst HIER und nicht als zweiter Knopf
                    neben "Vernetzen": zwei gleichrangige Knöpfe machten aus der
                    Karte eine Ja/Nein-Entscheidung über eine Person. Ohne
                    Rückfrage, weil die Geste nichts Endgültiges tut — anders als
                    Blockieren darunter. */}
                {onSnooze && (
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onSnooze(safeProfile.userId || safeProfile.id); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                  >
                    <Clock className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                    Später zeigen
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setShowReportModal(true); }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                >
                  <Flag className="w-4 h-4 text-danger" aria-hidden="true" />
                  Profil melden
                </button>
                <button
                  type="button"
                  onClick={handleBlockClick}
                  disabled={isBlocked}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground hover:bg-foreground/5 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                >
                  <Ban className="w-4 h-4 text-danger" aria-hidden="true" />
                  {isBlocked ? 'Blockiert' : 'Blockieren'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Avatar + Match-Ring.
            Die Groesse haengt an der HOEHE, nicht an der Breite (.profile-card-avatar
            in index.css). Eine breitenabhaengige Verkleinerung mit `sm:` waere falsch:
            der Umbruchpunkt liegt bei 640px, also oberhalb jedes Telefons — der Kopf
            schrumpfte dadurch auch dort, wo reichlich Hoehe war. Umgekehrt ist genau
            die Hoehe das Problem: Auf einem iPhone SE bleibt neben 144px Avatar kaum
            Platz fuer die Skills.
            Rahmen ist ein ruhiger Token-Rand statt Verlauf + lila Schein. */}
        <div ref={avatarRef} className="profile-card-avatar relative z-10 mx-auto">
          <div className="profile-card-avatar rounded-full p-[3px] bg-border">
            {stableImageUrl && !avatarFailed ? (
              <img
                src={stableImageUrl}
                alt={safeProfile.name}
                loading="eager"
                className="w-full h-full rounded-full object-cover"
                style={{ objectPosition: 'center 25%' }}
                onError={(e) => {
                  // Fallback-Kette: card-WebP -> Original -> lokale Initialen
                  if (fullImageUrl && e.target.src !== fullImageUrl && e.target.src.includes('/card-')) {
                    e.target.src = fullImageUrl;
                    return;
                  }
                  setAvatarFailed(true);
                }}
              />
            ) : (
              <Avatar name={safeProfile.name} size="fill" frame="none" />
            )}
          </div>

          {/* Match-Score Ring-Badge unten rechts.
              Auf dem kompakten Kopf zwei Anpassungen, beide gegen dasselbe
              Problem: Das Abzeichen haengt an der Ecke des QUADRATS um das
              Portrait, das Portrait ist aber ein Kreis. Wird der Kreis kleiner,
              schneidet dieselbe Ecke immer tiefer hinein — bei 86px Portrait
              verdeckte das unveraenderte 58px-Abzeichen ein Drittel des
              Gesichts. Also: eine Stufe kleiner (skaliert, statt einen zweiten
              Zahlensatz zu pflegen; der Ankerpunkt unten rechts bleibt stehen)
              und 8px weiter nach aussen, damit es wieder AM Rand des Kreises
              sitzt statt darin. Im Ergebnis dieselbe schmale Sichel wie auf der
              grossen Karte. */}
          {!hideMatchScore && (
          <div
            className={`absolute ${compactHeader ? '-bottom-2 -right-2' : '-bottom-1 -right-1'}`}
            style={compactHeader ? { transform: 'scale(0.82)', transformOrigin: '100% 100%' } : undefined}
          >
            <div className="relative w-[58px] h-[58px] rounded-full flex items-center justify-center bg-thumb shadow-token-md">
              <svg className="absolute inset-0 -rotate-90" width="58" height="58" viewBox="0 0 58 58" aria-hidden="true">
                <circle cx="29" cy="29" r={ringRadius} fill="none" stroke="rgb(var(--color-chip))" strokeWidth="4" />
                <circle
                  cx="29" cy="29" r={ringRadius} fill="none"
                  stroke="rgb(var(--color-primary))" strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringOffset}
                  style={{ transition: prefersReducedMotion ? 'none' : 'stroke-dashoffset 1.1s var(--ease-emphasis)' }}
                />
              </svg>
              <span className="relative text-[15px] font-bold tracking-tight text-foreground leading-none">
                <AnimatedNumber value={matchScore} from={0} duration={1100} />
                <span className="text-[9px] font-semibold">%</span>
              </span>
            </div>
          </div>
          )}
        </div>

        {/* Name + Meta */}
        <div className={`relative z-10 text-center ${compactHeader ? 'mt-2' : 'mt-3'}`}>
          {/* Kein Alter mehr (Aug 2026): In einer Schul-App sagt die Klasse eine
              Zeile darunter dasselbe, ohne ein persoenliches Datum zu zeigen.
              Der Name bricht um statt abzuschneiden (bis zwei Zeilen, Umbruch auch
              innerhalb langer Namen) — "David Schmidt" wurde vorher "David S…". */}
          <h2 className={`font-bold tracking-tight leading-tight text-foreground break-words line-clamp-2 ${compactHeader ? 'text-xl' : 'text-2xl'}`}>
            {safeProfile.name}
          </h2>
          {/* Zahlen erscheinen nur, wenn es etwas zu zeigen gibt: eine 0 neben dem
              Pokal wirbt fuer Leere. Ist alles leer, steht dort "Neu dabei". */}
          <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
            {/* Kein Level mehr in dieser Zeile (Aug 2026): Neben Klasse, Pokal, AP
                und Streak war es die fuenfte Angabe und damit eine zuviel — und
                die am wenigsten aussagekraeftige. Es steht weiter im Kopf der
                Rueckseite, wo Platz ist. */}
            {safeProfile.schoolClass && <span className="font-medium">Klasse {safeProfile.schoolClass}</span>}
            {completedSessions > 0 && (
              <span className="inline-flex items-center gap-1">
                <Trophy className="w-3 h-3 text-warning" aria-hidden="true" />{completedSessions}
              </span>
            )}
            {safeProfile.auraPoints > 0 && (
              <span className="inline-flex items-center gap-1">
                <AuraIcon className="w-3 h-3 text-primary" aria-hidden="true" />{safeProfile.auraPoints} AP
              </span>
            )}
            {currentStreak > 0 && (
              <span className="inline-flex items-center gap-1 text-warning">
                <Flame className="w-3 h-3" aria-hidden="true" />{currentStreak}T
              </span>
            )}
            {isNewcomer && (
              <span className="px-2 py-0.5 rounded-full bg-surface-raised border border-border font-medium">
                Neu dabei
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs Skills / Zeiten / Info (Glas-Segment) ───────────────────── */}
      <div className="px-5 flex-shrink-0 relative z-10">
        {/* Oktober 2026: ui/SegmentedControl statt Sonderbau — derselbe Umschalter
            wie im Werbespot (aktive Flaeche gleitet, Tinte statt Lila). Die Hoehe
            bleibt 44px, die Reihen-Rechnung der Karte (CARD_CHROME) gilt weiter. */}
        <SegmentedControl
          ariaLabel="Profilbereiche"
          value={activeTab}
          onChange={setActiveTab}
          options={tabs.map(({ id, label, Icon }) => ({ value: id, label, icon: Icon }))}
        />
      </div>

      {/* ── Tab-Inhalt ───────────────────────────────────────────────────── */}
      {/* Alle drei Tabs fuellen genau diese Flaeche und scrollen nicht: Die Karte
          hat eine feste Hoehe, also richtet sich der Inhalt nach ihr. Skills zeigt
          so viele Pill-Reihen, wie hier hineinpassen (gematchte zuerst) und fuehrt
          ueber "•••" auf die Rueckseite; Zeiten laesst das Wochenraster mitwachsen;
          Info laesst eine lange Beschreibung unten weich auslaufen. */}
      <div ref={contentRef} className="px-5 py-2 flex-1 min-h-0 flex flex-col relative z-10">

        {activeTab === 'skills' && (
          // Kein overflow-y-auto mehr: Die Reihenzahl (teaserRows) ist aus genau
          // dieser Hoehe berechnet, es kann also nichts ueberstehen. Was nicht in
          // die Vorschau passt, fuehrt ueber das "•••" auf die Rueckseite — das
          // ist der Weg zu allen Skills, nicht ein Scrollbalken in der Karte.
          //
          // Bleibt der Ausnahmefall: Reicht die Hoehe selbst fuer eine Reihe je
          // Kategorie nicht, schaltet die Karte auf Scrollen um (teaserScrolls).
          // Die Scrollleiste ist dabei versteckt — ohne den weichen Auslauf endet
          // der Inhalt an einer harten Kante und die angeschnittene Chip-Reihe
          // sieht nach Fehler aus. Genau so war es auf dem iPad zu sehen.
          <div ref={teaserPaneRef} className={`flex-1 min-h-0 space-y-4 ${teaserScrolls ? 'overflow-y-auto overscroll-contain scrollbar-hide fade-out-bottom' : 'overflow-hidden'}`}>
            <TeaserCategory
              label="Kann helfen bei"
              skills={safeProfile.offers}
              userMatchList={safeCurrentUser?.seeks}
              searchTerm={searchTerm}
              maxRows={teaserRows}
              onShowAll={flipToBack}
            />
            <TeaserCategory
              label="Möchte lernen"
              skills={safeProfile.seeks}
              userMatchList={safeCurrentUser?.offers}
              searchTerm={searchTerm}
              maxRows={teaserRows}
              onShowAll={flipToBack}
            />
          </div>
        )}

        {activeTab === 'times' && (
          // Der Kasten war bis August 2026 so hoch wie die Karte, das Raster darin
          // aber nur rund 200px — darunter blieb ein leeres Feld, und bei knapper
          // Hoehe scrollte er. Jetzt waechst das Raster mit: feste Kopfzeile, fuenf
          // gleich hohe Zeitzeilen (1fr), Legende darunter. Nichts scrollt mehr.
          //
          // September 2026: Die Zeilen hatten eine Mindesthoehe von 1.25rem. Auf
          // dem iPhone (375px, Karte ~490px hoch) braucht das Raster damit rund
          // 140px, zur Verfuegung stehen aber nur ~100px — die Differenz lief aus
          // dem Rasterkasten heraus und legte die letzte Zeile ("Nach") ueber die
          // Legende. Jetzt duerfen die Zeilen mitschrumpfen (minmax(0, 1fr)), das
          // Raster schneidet einen Rest sicherheitshalber ab, und die Beschriftung
          // steht ohne Zeilenabstand, damit sie auch in eine schmale Zeile passt.
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-xl p-3 bg-surface-raised border border-border shadow-token-sm">
            <div
              className="grid grid-cols-6 gap-1 text-xs flex-1 min-h-0 overflow-hidden"
              style={{ gridTemplateRows: `auto repeat(${timeSlots.length}, minmax(0, 1fr))` }}
            >
              <div />
              {weekDays.map((day) => (
                <div key={day} className="text-center font-semibold leading-none text-muted-foreground pb-1">{day}</div>
              ))}
              {timeSlots.map((slot) => (
                <React.Fragment key={slot.label}>
                  <div className="flex items-center justify-end overflow-hidden pr-1.5 leading-none text-muted-foreground">{slot.label}</div>
                  {weekDays.map((day) => {
                    const slotKey = `${day}-${slot.label}`;
                    const isProfileActive = isTimeSlotActive(safeProfile.availability, day, slot.hours);
                    const isShared = sharedSlots.has(slotKey);
                    return (
                      <div
                        key={slotKey}
                        className={`rounded h-full min-h-[0.5rem] ${
                          isShared ? 'bg-primary'
                            : isProfileActive ? 'bg-primary/40' : 'bg-border/50'
                        }`}
                        title={isShared ? 'Gemeinsame Zeit' : isProfileActive ? 'Verfügbar' : 'Nicht verfügbar'}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            <div className="flex shrink-0 items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-primary" />Gemeinsam
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-primary/40" />Verfügbar
              </span>
            </div>
          </div>
        )}

        {activeTab === 'info' && (
          // Oktober 2026: Die Aura-Box stand fest am Fuss, darueber "Keine
          // Beschreibung hinterlegt" in einer leeren Flaeche — ohne Beschreibung
          // (der Normalfall) wirkte der Reiter verloren. Jetzt steht die Aura-Box
          // oben direkt unter den Reitern; die Beschreibung folgt darunter, nimmt
          // den Rest und laeuft unten weich aus (.fade-bottom in index.css). Fehlt
          // sie, steht dort nichts statt eines Platzhaltersatzes.
          <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
            <div className="shrink-0 rounded-xl p-4 bg-surface-raised border border-border shadow-token-sm flex items-center gap-3">
              {showOtter && (
                <OtterFigure
                  level={currentLevel}
                  parts={otterParts}
                  size={64}
                  title={`Otter von ${safeProfile.name || safeProfile.displayName || 'dieser Person'}, Level ${currentLevel}`}
                  className="shrink-0"
                />
              )}
              <div className="min-w-0 flex-1">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-muted-foreground">Aura-Fortschritt</span>
                <span className="text-sm font-semibold text-primary">{safeProfile.auraPoints} AP</span>
              </div>
              <div className="w-full bg-border/40 rounded-full h-2 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${auraProgress.progressPercentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {auraProgress.isMaxLevel
                  ? 'Höchstes Level erreicht'
                  : `Noch ${auraProgress.pointsToNextLevel} AP bis Level ${auraProgress.nextLevel}`}
              </p>
              </div>
            </div>
            {safeProfile.bio && (
              <p className="flex-1 min-h-0 overflow-hidden fade-bottom text-sm leading-relaxed text-muted-foreground">{safeProfile.bio}</p>
            )}
          </div>
        )}
      </div>

      {/* ── Vernetzen-Button (volle Breite, Hauptaktion der Karte) ───────── */}
      {/* Nutzte bis Juli 2026 SharedStyles.buttonMagicSubtle: Verlaufsflaeche,
          Verlaufs-Hover und farbiger Schatten — ein Verstoss gegen die
          De-Slop-Regel "keine Verlaeufe in Buttons/CTAs" (TOKENS.md). Jetzt der
          kanonische Primaerbutton; "Anfrage annehmen" bekommt die Success-Variante
          statt gruenem Text auf lila Flaeche. Der Deaktiviert-Zustand kommt aus den
          disabled:-Klassen der Variante, damit die Geometrie nie springt. */}
      {!hideConnectButton && (
      <div className="px-5 pb-5 pt-1 flex-shrink-0 relative z-10">
        <button
          type="button"
          onClick={() => {
            if (isPreviewMode) return;
            if (connectionStatus === 'connected') {
              navigate(connectionChatId ? `/app?view=chat&chatId=${connectionChatId}` : '/app?view=chat-overview');
            } else if (connectionStatus === 'pending_received') {
              handleAcceptClick();
            } else {
              handleConnectClick();
            }
          }}
          disabled={!isPreviewMode && (!hasValidUserId || connectionStatus === 'pending_sent' || isAccepting)}
          className={buttonClasses(
            connectionStatus === 'pending_received' ? 'success' : 'primary',
            'md',
            'overflow-hidden',
            true
          )}
        >
          {/* Der Text wechselt wie im Spot: der alte rutscht nach oben weg, der
              neue kommt von unten. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={connectionStatus}
              className="inline-flex items-center gap-2"
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.22 }}
            >
              {connectionStatus === 'connected' ? <><MessageCircle className="w-4 h-4" aria-hidden="true" /> Zum Chat</>
                : connectionStatus === 'pending_sent' ? <><Check className="w-4 h-4" aria-hidden="true" /> Anfrage gesendet</>
                  : connectionStatus === 'pending_received' ? <><CheckCircle className="w-4 h-4" aria-hidden="true" /> Anfrage annehmen</>
                    : <>Vernetzen <ChevronRight className="w-4 h-4" aria-hidden="true" /></>}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>
      )}

      </div>{/* /Vorderseite */}

      {/* ═══ RÜCKSEITE — Alle Skills ═══════════════════════════════════════ */}
      <div
        className="absolute inset-0 rounded-3xl overflow-hidden flex flex-col bg-surface border border-border shadow-token-lg"
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
          WebkitTransform: 'rotateY(180deg)',
          opacity: isFlipped ? 1 : 0,
          transition: prefersReducedMotion ? 'none' : 'opacity 0s linear 0.3s',
          pointerEvents: isFlipped ? 'auto' : 'none'
        }}
      >
        {/* Kompakter Kopf + Schliessen-Knopf */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-surface-raised flex-shrink-0">
          <Avatar
            name={safeProfile.name}
            src={stableImageUrl}
            size="md"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-foreground truncate">
              {safeProfile.name}
            </h3>
            <p className="text-xs text-muted-foreground">Alle Skills · Lvl {currentLevel}</p>
          </div>
          <button
            type="button"
            onClick={flipToFront}
            aria-label="Zurück zur Vorderseite"
            className="relative p-2 before:absolute before:-inset-1.5 before:content-[''] rounded-full bg-surface border border-border text-muted-foreground hover:text-foreground flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {/* X statt gedrehtem Pfeil: Die Rueckseite ist etwas Geoeffnetes, das
                man wieder zumacht — kein Schritt, den man rueckgaengig macht. */}
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        {/* Alle Skills, scrollbar */}
        {/* Die Rueckseite DARF scrollen — hier steht bewusst alles. Nur der graue
            Systembalken bleibt weg (scrollbar-hide), damit die Karte auch beim
            Blaettern keine harte Kante bekommt; overscroll-contain haelt die
            Geste in der Karte, statt den Kartenstapel dahinter mitzuziehen. */}
        <div className="px-5 py-4 flex-1 overflow-y-auto overscroll-contain scrollbar-hide space-y-5">
          <AllSkillsList label="Kann helfen bei" skills={safeProfile.offers} userMatchList={safeCurrentUser?.seeks} searchTerm={searchTerm} />
          <AllSkillsList label="Möchte lernen" skills={safeProfile.seeks} userMatchList={safeCurrentUser?.offers} searchTerm={searchTerm} />
        </div>
      </div>

      </div>{/* /Dreh-Container */}
    </div>{/* /Perspektive-Bühne */}

    {/* Rückfrage vor dem Blockieren — ebenfalls außerhalb der 3D-Bühne */}
    <Modal.Confirm
      open={blockConfirmOpen}
      onClose={() => setBlockConfirmOpen(false)}
      onConfirm={confirmBlock}
      variant="danger"
      title={`${safeProfile.name || 'Diese Person'} blockieren?`}
      confirmLabel="Blockieren"
    >
      Ihr könnt euch dann keine Chat-Anfragen mehr schicken und seht euch nicht mehr im Matching.
      In den Einstellungen kannst du die Blockierung wieder aufheben.
    </Modal.Confirm>

    {/* Melde-Formular außerhalb der 3D-Bühne — sonst schränkt die transform-Kette
        das fixed-Overlay auf die Kartenfläche ein. */}
    {hasValidUserId && (
      <ConnectSheet
        open={connectSheetOpen}
        onClose={() => setConnectSheetOpen(false)}
        recipient={{
          id: safeProfile.userId || safeProfile.tenant_users?.id,
          name: safeProfile.name,
          image: safeProfile.image || safeProfile.avatar || '',
        }}
        onSent={() => setLocalConnection({ status: 'pending_sent' })}
        origin={origin}
      />
    )}

    {showReportModal && (
      <ReportModal
        targetType="PROFILE"
        targetId={safeProfile.id}
        reportedUserId={safeProfile.userId || safeProfile.tenant_users?.id}
        targetLabel={safeProfile.name}
        onClose={() => setShowReportModal(false)}
      />
    )}
    </>
  );
};

ProfileCardV2.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
    image: PropTypes.string,
    auraLevel: PropTypes.number,
    auraPoints: PropTypes.number,
    offers: PropTypes.array,
    seeks: PropTypes.array,
    auraStats: PropTypes.object,
    bio: PropTypes.string,
    schoolClass: PropTypes.string,
    availability: PropTypes.object,
    matchScore: PropTypes.number
  }).isRequired,
  currentUser: PropTypes.object,
  searchTerm: PropTypes.string,
  isPreviewMode: PropTypes.bool,
  hideMatchScore: PropTypes.bool,
  hideConnectButton: PropTypes.bool,
  heightClass: PropTypes.string,
  fill: PropTypes.bool,
  onSnooze: PropTypes.func,
  onBlocked: PropTypes.func,
  /** Herkunft einer Vernetzungsanfrage (ROUND | SEARCH | SKILL_BROWSE | PROFILE_LINK | OTHER) */
  origin: PropTypes.oneOf(['ROUND', 'SEARCH', 'SKILL_BROWSE', 'PROFILE_LINK', 'OTHER'])
};

const MemoizedProfileCardV2 = memo(ProfileCardV2, (prev, next) => (
  prev.profile?.id === next.profile?.id
  && prev.profile?.matchScore === next.profile?.matchScore
  && prev.currentUser?.id === next.currentUser?.id
  && prev.searchTerm === next.searchTerm
  && prev.origin === next.origin
));

export { MemoizedProfileCardV2 as ProfileCardV2 };
export default MemoizedProfileCardV2;
